import React from "react";
import { FE_BASE_URL } from "./config";
import { Cover } from "../constants/cover";
import { Mode } from "../constants/mode";
import {
  CourseCarouselItem,
  CourseDate,
  DropdownOptions,
  Employee,
  ReservedCourse,
} from "../types";
import {
  Ambulance,
  FireExtinguisher,
  HardHat,
  Tractor,
  TriangleAlert,
  Utensils,
} from "lucide-react";
import { Color } from "../constants/color";
import { TFunction } from "i18next";
import { Category } from "../constants/categories";
import { Status } from "../constants/status";
import { opzioniSede } from "../features/certificate/pipeline";

export function goToPage(
  path: string,
  param?: string,
  query?: Record<string, string | number>
) {
  const queryString = query
    ? "?" +
      new URLSearchParams(
        Object.entries(query).map(([chiave, valore]) => [
          chiave,
          String(valore),
        ])
      ).toString()
    : "";
  if (param) {
    window.location.assign(FE_BASE_URL + `/${path}/${param}` + queryString);
  } else {
    window.location.assign(FE_BASE_URL + `/${path}` + queryString);
  }
}

export function getCourseCover(tipo: number): string {
  return Cover.get(tipo)?.icon as string;
}

export function getCourseMode(mode: number): string {
  return Mode.get(mode)?.label as string;
}

export function getDateDuration(dates: CourseDate[]): number {
  if (dates.length === 1) {
    const day1Start = dates[0].start.toString().split(":");
    const day1End = dates[0].end.toString().split(":");
    const day1StartTime = new Date().setHours(
      Number(day1Start[0]),
      Number(day1Start[1])
    );
    const day1EndTime = new Date().setHours(
      Number(day1End[0]),
      Number(day1End[1])
    );
    const day1duration = day1EndTime.valueOf() - day1StartTime.valueOf();
    let h = Math.floor(day1duration / 1000 / 60 / 60);
    const m = Math.floor((day1duration / 1000 / 60 / 60 - h) * 60);
    if (m !== 0) h = h + m;
    return h;
  }
  const day1Start = dates[0].start.toString().split(":");
  const day1End = dates[0].end.toString().split(":");
  const day2Start = dates[1].start.toString().split(":");
  const day2End = dates[1].end.toString().split(":");
  const day1StartTime = new Date().setHours(
    Number(day1Start[0]),
    Number(day1Start[1])
  );
  const day1EndTime = new Date().setHours(
    Number(day1End[0]),
    Number(day1End[1])
  );
  const day2StartTime = new Date().setHours(
    Number(day2Start[0]),
    Number(day2Start[1])
  );
  const day2EndTime = new Date().setHours(
    Number(day2End[0]),
    Number(day2End[1])
  );

  const day1duration = day1EndTime.valueOf() - day1StartTime.valueOf();
  const day2duration = day2EndTime.valueOf() - day2StartTime.valueOf();
  const msDuration = day1duration + day2duration;
  let h = Math.floor(msDuration / 1000 / 60 / 60);
  const m = Math.floor((msDuration / 1000 / 60 / 60 - h) * 60);
  if (m !== 0) h = h + m;
  return h;
}

export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function mapCourseDate(
  availableDates: Set<CourseDate>
): Map<number, CourseDate[]> {
  const mappedDates: Map<number, CourseDate[]> = new Map<
    number,
    CourseDate[]
  >();
  availableDates.forEach((el) => {
    const array = mappedDates.get(el.variationId) ?? [];
    array.push(el);
    mappedDates.set(el.variationId, array);
  });
  return mappedDates;
}

export const dateOptions: Intl.DateTimeFormatOptions = {
  weekday: "short",
  year: "numeric",
  month: "long",
  day: "numeric",
};

export const dateOptionDayOnly: Intl.DateTimeFormatOptions = {
  day: "numeric",
};

export const dateOptionWeekDayOnly: Intl.DateTimeFormatOptions = {
  weekday: "short",
};

export const dateOptionsMonthOnly: Intl.DateTimeFormatOptions = {
  month: "long",
};

export const dateOptionsReserved: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "numeric",
  year: "2-digit",
};

export const dateOptionsShort: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
};

export const dateOptionsRange: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
};
export const dateOptionsNumeric: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "numeric",
  day: "numeric",
};

export function getLocale(): string {
  return localStorage.getItem("i18nextLng")!;
}

export function getCourseIcon(courseType: number) {
  let icon = <HardHat color={Color.LIGHT_BLUE} size={48} />;
  switch (courseType) {
    case 1:
      icon = <FireExtinguisher color={Color.LIGHT_BLUE} size={48} />;
      break;
    case 2:
      icon = <Ambulance color={Color.LIGHT_BLUE} size={48} />;
      break;
    case 3:
      icon = <HardHat color={Color.LIGHT_BLUE} size={48} />;
      break;
    case 4:
      icon = <Utensils color={Color.LIGHT_BLUE} size={48} />;
      break;
    case 5:
      icon = <Tractor color={Color.LIGHT_BLUE} size={48} />;
      break;
    case 6:
      icon = <TriangleAlert color={Color.LIGHT_BLUE} size={48} />;
      break;
    default:
      break;
  }
  return icon;
}

export function initHeadquartersFilterBook(
  courses: CourseCarouselItem[],
  setHeadquarters: (
    value:
      | ((
          prevState: Set<DropdownOptions> | undefined
        ) => Set<DropdownOptions> | undefined)
      | Set<DropdownOptions>
      | undefined
  ) => void,
  t: TFunction<"translation", undefined>
) {
  const cities: Set<string> = new Set();

  courses.forEach((el: CourseCarouselItem) => {
    // Rimuovo spazi all'inizio e alla fine e sostituisco eventuali spazi multipli con uno singolo
    const cleanedCity = el.city.trim().replace(/\s+/g, " ");
    // Normalizzo la stringa rendendola maiuscola per la visualizzazione
    const normalizedCity = cleanedCity.toUpperCase();
    cities.add(normalizedCity);
  });

  initHeadquartersFilter(cities, setHeadquarters, t);
}

export function initHeadquartersFilterDates(
  courses: CourseDate[],
  setHeadquarters: (
    value:
      | ((
          prevState: Set<DropdownOptions> | undefined
        ) => Set<DropdownOptions> | undefined)
      | Set<DropdownOptions>
      | undefined
  ) => void,
  t: TFunction<"translation", undefined>
) {
  const cities: Set<string> = new Set();
  courses.forEach((el: CourseDate) => cities.add(el.city.toUpperCase()));
  initHeadquartersFilter(cities, setHeadquarters, t);
}

export function initHeadquartersFilterEmployee(
  employees: Employee[],
  setHeadquarters: (
    value:
      | ((
          prevState: Set<DropdownOptions> | undefined
        ) => Set<DropdownOptions> | undefined)
      | Set<DropdownOptions>
      | undefined
  ) => void,
  t: TFunction<"translation", undefined>
) {
  // Le sedi dei dipendenti arrivano dal testo libero della "Sede di lavoro":
  // la stessa sede compare scritta in modi che a schermo sembrano identici
  // (spazi ai bordi, maiuscole diverse) e ci sono dipendenti senza sede. La
  // pipeline degli attestati fa gia' collassare quelle varianti e scartare i
  // vuoti, quindi qui si riusa `opzioniSede` invece di rifare la normalizzazione.
  // Si mostra la chiave normalizzata (senza spazi ai bordi, maiuscola) perche'
  // e' anche il valore che il filtro confronta per contenuto su `workplace`:
  // cosi' una sola voce continua a trovare i dipendenti di tutte le varianti.
  const opzioni = opzioniSede(
    employees.map((el: Employee) => el.workplace ?? ""),
    t("book-course-filter-all")
  );
  const headQuarterSet: Set<DropdownOptions> = new Set();
  headQuarterSet.add({
    value: t("book-course-filter-all"),
    label: t("book-course-filter-all"),
  });
  Object.keys(opzioni)
    .filter((chiave) => chiave !== "")
    .sort((a, b) => a.localeCompare(b))
    .forEach((chiave) => headQuarterSet.add({ value: chiave, label: chiave }));
  setHeadquarters(headQuarterSet);
}

export function initHeadquartersFilterReserved(
  courses: ReservedCourse[],
  setHeadquarters: (
    value:
      | ((
          prevState: Set<DropdownOptions> | undefined
        ) => Set<DropdownOptions> | undefined)
      | Set<DropdownOptions>
      | undefined
  ) => void,
  t: TFunction<"translation", undefined>
) {
  const cities: Set<string> = new Set();
  courses.forEach((el: ReservedCourse) =>
    el.date.forEach((date) => cities.add(date.city.toUpperCase()))
  );
  initHeadquartersFilter(cities, setHeadquarters, t);
}

function initHeadquartersFilter(
  cities: Set<string>,
  setHeadquarters: (
    value:
      | ((
          prevState: Set<DropdownOptions> | undefined
        ) => Set<DropdownOptions> | undefined)
      | Set<DropdownOptions>
      | undefined
  ) => void,
  t: TFunction<"translation", undefined>
) {
  /*
   La voce «Tutti» sta FUORI dal ciclo, e le sedi sono ordinate.

   Stava dentro, aggiunta quando l'indice valeva zero: su un'azienda senza corsi il ciclo non
   partiva e la tendina restava senza nemmeno quella voce — è il difetto che il cliente ha
   segnalato su SICURNET, «i filtri SEDE e MESE si aprono privi di opzioni» (294.8). Un elenco
   di scelte deve poter essere vuoto senza perdere il modo di dire «non filtrare».

   L'ordinamento risponde a BUG-20: le sedi erano alfabetiche in Gestione corsi e in ordine di
   arrivo qui, perché là l'ordine lo dava il backend e qui nessuno. Ora non dipende da chi
   consegna i dati.
  */
  const headQuarterSet: Set<DropdownOptions> = new Set();
  headQuarterSet.add({
    value: t("book-course-filter-all"),
    label: t("book-course-filter-all"),
  });
  Array.from(cities)
    .filter((city) => city.trim() !== "")
    .sort((a, b) => a.localeCompare(b))
    .forEach((city) => headQuarterSet.add({ value: city, label: city }));

  setHeadquarters(headQuarterSet);
}

export function initCategoryFilter(
  courses: CourseCarouselItem[],
  setCategories: (
    value:
      | ((
          prevState: Set<DropdownOptions> | undefined
        ) => Set<DropdownOptions> | undefined)
      | Set<DropdownOptions>
      | undefined
  ) => void,
  t: TFunction<"translation", undefined>
) {
  const categories: Set<number> = new Set();
  categories.add(0);
  courses.forEach((el: CourseCarouselItem) => categories.add(el.type));
  const categorySet: Set<DropdownOptions> = new Set();
  categorySet.add({
    value: "0".toString(),
    label: t("book-course-filter-all"),
  });
  for (let i = 0; i < Category.size; i++) {
    categorySet.add({
      value: (i + 1).toString(),
      label: Category.get(i + 1)?.label,
    } as DropdownOptions);
  }
  setCategories(categorySet);
}

export function initLectureModes(
  courses: CourseDate[],
  setLectureModes: (
    value:
      | ((
          prevState: Set<DropdownOptions> | undefined
        ) => Set<DropdownOptions> | undefined)
      | Set<DropdownOptions>
      | undefined
  ) => void,
  t: TFunction<"translation", undefined>
) {
  // La tendina offre le modalita' che le date del corso hanno davvero: si
  // cicla su quelle raccolte, non su un indice contato su quante sono, che
  // altrimenti proporrebbe "In Presenza" a un corso di sole videoconferenze.
  const lectureModes: Set<number> = new Set();
  courses.forEach((el: CourseDate) => lectureModes.add(el.mode));
  const lectureModeSet: Set<DropdownOptions> = new Set();
  lectureModeSet.add({
    value: t("book-course-filter-all"),
    label: t("book-course-filter-all"),
  });
  Array.from(lectureModes)
    .sort((a, b) => a - b)
    .forEach((modalita) => {
      const label = Mode.get(modalita)?.label;
      if (!label) return;
      lectureModeSet.add({ value: t(label), label: t(label) });
    });
  setLectureModes(lectureModeSet);
}

export function initMonthsFilter(
  setMonths: (
    value:
      | ((
          prevState: Set<DropdownOptions> | undefined
        ) => Set<DropdownOptions> | undefined)
      | Set<DropdownOptions>
      | undefined
  ) => void,
  t: TFunction<"translation", undefined>
) {
  const monthSet: Set<DropdownOptions> = new Set();
  for (let i = 0; i < 13; i++) {
    if (i === 0) {
      monthSet.add({ value: "0", label: t("book-course-filter-all") });
      continue;
    }
    monthSet.add({ value: i.toString(), label: t(`months.${i}`) });
  }
  setMonths(monthSet);
}

export function initMonthsFilterByArchiveStatus(
  courses: ReservedCourse[],
  archiveStatus: "archived" | "upcoming",
  setMonths: (
    value:
      | ((
          prevState: Set<DropdownOptions> | undefined
        ) => Set<DropdownOptions> | undefined)
      | Set<DropdownOptions>
      | undefined
  ) => void,
  t: TFunction<"translation", undefined>
) {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // getMonth() returns 0-11, we need 1-12
  const availableMonths = new Set<number>();

  // Extract all months from course dates, considering year and month
  courses.forEach((course) => {
    Array.from(course.date).forEach((date) => {
      const courseDate = new Date(date.date);
      const courseYear = courseDate.getFullYear();
      const courseMonth = courseDate.getMonth() + 1;

      // Compare year and month together
      const courseYearMonth = courseYear * 100 + courseMonth; // e.g., 202410 for Oct 2024
      const currentYearMonth = currentYear * 100 + currentMonth;

      if (
        (archiveStatus === "upcoming" && courseYearMonth >= currentYearMonth) ||
        (archiveStatus === "archived" && courseYearMonth < currentYearMonth)
      ) {
        availableMonths.add(courseMonth);
      }
    });
  });

  // Build month set based on archive status
  const monthSet: Set<DropdownOptions> = new Set();
  monthSet.add({ value: "0", label: t("book-course-filter-all") });

  // Sort and add months
  Array.from(availableMonths)
    .sort((a, b) => a - b)
    .forEach((month) => {
      monthSet.add({ value: month.toString(), label: t(`months.${month}`) });
    });

  setMonths(monthSet);
}

export function initModesFilter(
  setModes: (
    value:
      | ((
          prevState: Set<DropdownOptions> | undefined
        ) => Set<DropdownOptions> | undefined)
      | Set<DropdownOptions>
      | undefined
  ) => void,
  t: TFunction<"translation", undefined>
) {
  // Una voce per ogni modalita' nota, piu' la voce "tutte": si cicla sulle
  // chiavi di `Mode`, non su un indice contato sulla sua dimensione, altrimenti
  // l'ultima modalita' (l'e-learning) non comparirebbe mai.
  const modesSet: Set<DropdownOptions> = new Set();
  modesSet.add({ value: "0", label: t("book-course-filter-all") });
  Array.from(Mode.keys())
    .sort((a, b) => a - b)
    .forEach((modalita) => {
      modesSet.add({
        value: modalita.toString(),
        label: Mode.get(modalita)!.label,
      });
    });
  setModes(modesSet);
}

export function initEmployeeStateFilter(
  setEmployeeStates: (
    value:
      | ((
          prevState: Set<DropdownOptions> | undefined
        ) => Set<DropdownOptions> | undefined)
      | Set<DropdownOptions>
      | undefined
  ) => void,
  t: TFunction<"translation", undefined>
) {
  const stateSet: Set<DropdownOptions> = new Set();
  for (let i = 0; i <= Status.size; i++) {
    if (i === 0) {
      stateSet.add({
        value: t("book-course-filter-all"),
        label: t("book-course-filter-all"),
      });
      continue;
    }
    stateSet.add({
      value: Status.get(i)?.label,
      label: Status.get(i)?.label,
    } as DropdownOptions);
  }
  setEmployeeStates(stateSet);
}

/*
 La mansione ridotta alla sua forma di confronto: senza spazi ai bordi, maiuscola.

 Come la sede di lavoro, la mansione e' testo libero, e la stessa mansione finisce in anagrafica
 scritta in modi che a schermo sembrano identici. Su WETECH'S la tendina elencava `IMPIEGATO` e
 `IMPIEGATO ` come due voci distinte, e lo stesso per `IMPIEGATO AMMINISTRATIVO`.

 **Chi usa questa chiave per costruire la tendina deve usarla anche per filtrare.** I due filtri
 confrontano la mansione per uguaglianza, non per contenuto: se la tendina offrisse la sola forma
 normalizzata e il confronto restasse sul dato grezzo, scegliere `IMPIEGATO` non troverebbe piu' i
 dipendenti scritti `IMPIEGATO ` — che e' peggio del doppione, perche' li fa sparire in silenzio.

 Restano fuori dalla sua portata i refusi veri (`TECNICO PROGRAMMETORE` accanto a `TECNICO
 PROGRAMMATORE`): quelli sono dati da bonificare, non varianti di scrittura.
*/
export function chiaveMansione(mansione?: string | null): string {
  return (mansione ?? "").trim().toUpperCase();
}

export function initJobDescriptionsFilter(
  employeeList: Employee[],
  setJobDescriptions: (
    value:
      | ((
          prevState: Set<DropdownOptions> | undefined
        ) => Set<DropdownOptions> | undefined)
      | Set<DropdownOptions>
      | undefined
  ) => void,
  t: TFunction<"translation", undefined>
) {
  const jobDescriptions: Set<string> = new Set();
  employeeList.forEach((el: Employee) => {
    const chiave = chiaveMansione(el.jobDescription);
    if (chiave) jobDescriptions.add(chiave);
  });
  const jobDescriptionsSet: Set<DropdownOptions> = new Set();
  jobDescriptionsSet.add({
    value: t("book-course-filter-all"),
    label: t("book-course-filter-all"),
  });
  Array.from(jobDescriptions)
    .sort((a, b) => a.localeCompare(b))
    .forEach((chiave) =>
      jobDescriptionsSet.add({ value: chiave, label: chiave })
    );
  setJobDescriptions(jobDescriptionsSet);
}

export function getCFListFromEmployees(employeeList: Employee[]): string[] {
  const cfList: string[] = [];
  employeeList.forEach((el) => cfList.push(el.cf));
  return cfList;
}

export function getEmployeesFromCFList(
  employeeList: Employee[],
  cfList: string[]
): Employee[] {
  const newList: Employee[] = [];
  employeeList.forEach((el) => {
    if (cfList.includes(el.cf)) {
      newList.push(el);
    }
  });
  return newList;
}

export function filterParticipantAlreadyReserved(
  employeeList: Employee[],
  participantsList: Employee[],
  toRemove: Employee[]
): Employee[] {
  const newList: Employee[] = [];
  let notNew: boolean = true;
  employeeList.forEach((el) => {
    let isPresent = false;
    participantsList.forEach((pr) => {
      if (pr.cf === el.cf) {
        isPresent = true;
        notNew = false;
      }
    });
    toRemove.forEach((pr) => {
      if (pr.cf === el.cf) {
        isPresent = true;
        notNew = false;
      }
    });
    if (!isPresent) {
      newList.push(el);
      notNew = false;
    }
  });
  return newList.length !== 0 || !notNew ? newList : employeeList;
}

export function orderReservationByDate(
  reservationList: ReservedCourse[]
): ReservedCourse[] {
  reservationList.sort((a, b) => {
    const dateList1 = Array.from(a.date)[0].date;
    const dateList2 = Array.from(b.date)[0].date;
    let first = 1;
    if (dateList1 < dateList2) first = -1;
    return first;
  });
  return reservationList;
}
